import type { DesignDocument } from '@repo/api-contracts';
import { useState } from 'react';
import {
  clearDraft,
  clearDraftPreview,
  loadDraft,
  loadDraftPreview,
} from '@/features/design-editor';

interface GuestRequestDraftTarget {
  readonly productId: string;
  readonly colorOptionId: string;
}

interface GuestRequestDraft {
  /** 같은 상품·같은 색상의 에디터 임시 작업 — 없으면(다른 기기·이미 접수·주소 오류) null. */
  readonly draft: DesignDocument | null;
  /** 에디터가 [견적 요청하기] 때 남긴 앞면 미리보기 그림(이미지 data URL) — 임시 작업이 있을 때만 읽고, 없거나 깨졌으면 null. */
  readonly previewSrc: string | null;
  /** 접수가 끝나면 임시 작업과 미리보기를 함께 지운다. 이미 읽은 값은 그대로라 완료 화면은 흔들리지 않는다. */
  readonly clearAfterSubmit: () => void;
}

/** 임시 작업은 요청 주소의 상품·색상과 같을 때만 쓴다 — 다른 색상으로 만든 디자인으로 요청하지 않는다. */
const readDraft = (target: GuestRequestDraftTarget | null): DesignDocument | null => {
  if (target === null) return null;
  const draft = loadDraft(target.productId);
  return draft !== null && draft.colorOptionId === target.colorOptionId ? draft : null;
};

/**
 * 비회원 요청 화면이 쓰는 에디터 임시 작업(비회원 견적 설계 G12·G13) — 읽기(소유 규칙)와 접수 뒤 정리를 한곳에 둔다.
 * 처음 열 때 한 번만 읽는다(접수 뒤 저장소를 지워도 완료 화면은 그대로여야 한다). 상품·색상을 모르면 저장소를 건드리지 않는다.
 */
export const useGuestRequestDraft = (target: GuestRequestDraftTarget | null): GuestRequestDraft => {
  const [snapshot] = useState(() => {
    const draft = readDraft(target);
    const previewSrc =
      draft !== null && target !== null ? loadDraftPreview(target.productId) : null;
    return { draft, previewSrc };
  });

  const clearAfterSubmit = (): void => {
    if (target === null) return;
    clearDraft(target.productId);
    clearDraftPreview(target.productId);
  };

  return { ...snapshot, clearAfterSubmit };
};
